import invariant from 'invariant';

import { RouteModule } from '#/horizon/components/RouteModule';
import { useEmbedSearchWidget } from '#/modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import { embedSearchWidgetApiIsomorphicFetchJob } from '#/modules/embedSearchWidget/jobs/api/embedSearchWidgetApiFetchJob';
import type { embedSearchWidgetViewJobRoute } from '#/modules/embedSearchWidget/jobs/routing/embedSearchWidgetJobs.routes';
import type { EmbedSearchWidgetLayoutHandleProps } from '#/modules/embedSearchWidget/layout/EmbedSearchWidgetLayoutHandleProps';
import { SearchJobsRefinements } from '#/modules/search/jobs/components/Refinements/SearchJobsRefinements';
import { NotFoundPageLoadable } from '#/pages/NotFound/NotFoundPageLoadable';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';

import { EmbedSearchWidgetViewJobPageLoadable } from './EmbedSearchWidgetViewJobPageLoadable';

export const handle = {
  searchRefinements: <SearchJobsRefinements />,
  isSearchPage: false,
} satisfies EmbedSearchWidgetLayoutHandleProps;

export async function loader({
  request,
  context,
  params: { embedSearchWidgetId, jobId },
}: HorizonAppRouteLoaderArgs<typeof embedSearchWidgetViewJobRoute>) {
  try {
    const [job] = await Promise.all([
      embedSearchWidgetApiIsomorphicFetchJob({ request, context })(
        embedSearchWidgetId,
        jobId,
      ),
      EmbedSearchWidgetViewJobPageLoadable.load(),
    ]);

    if (job.type === 'UNPUBLISHED_JOB') return idealistNotFound(null);

    return { type: 'JOB', job } as const;
  } catch {
    return idealistNotFound(null);
  }
}

export default function EmbedSearchWidgetViewJobHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { embedSearchWidget } = useEmbedSearchWidget();
  invariant(embedSearchWidget, 'Embed search widget not set.');

  switch (loaderData?.type) {
    case undefined:
      return <NotFoundPageLoadable />;
    case 'JOB':
      return (
        <RouteModule title={loaderData.job.name}>
          <EmbedSearchWidgetViewJobPageLoadable
            job={loaderData.job}
            embedSearchWidget={embedSearchWidget}
          />
        </RouteModule>
      );
  }
}
